import { useCallback, useRef, useState, useEffect } from 'react';
import { BasePaymentOption, validatePaymentOptions } from '@/types/pricing';
import { DeliveryMode } from '@/types/program';
import { PricingService, PricingOperationResult } from '@/services/pricingService';
import { PaymentOptionsAPI, CreatePaymentOptionRequest, UpdatePaymentOptionsRequest } from '@/lib/api/payment-options';
import { toast } from 'sonner';

export interface UsePaymentOptionsReturn {
  updatePaymentOptions: (paymentOptions: BasePaymentOption[]) => Promise<PricingOperationResult>;
  createPaymentOptions: (paymentOptions: BasePaymentOption[]) => Promise<PricingOperationResult>;
  updateTemporaryPlans: (finalProgramId: string) => Promise<PricingOperationResult>;
  rollbackLastOperation: () => PricingOperationResult;
  getValidationErrors: () => string[];
  getValidationWarnings: () => string[];
  isConfigured: (programType: DeliveryMode) => boolean;
  getPricingForType: (programType: DeliveryMode) => any;
  validateAllPricing: () => { isValid: boolean; errors: string[]; warnings: string[] };
  isLoading: boolean;
}

export const usePaymentOptions = (
  formData: any, 
  setFormData: any
): UsePaymentOptionsReturn => {
  const pricingServiceRef = useRef<PricingService | null>(null);
  const [lastOperationResult, setLastOperationResult] = useState<PricingOperationResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Initialize pricing service if not already done - use stable references
  const getPricingService = useCallback((): PricingService => {
    if (!pricingServiceRef.current) {
      try {
        const initialState = {
          typeSpecificDataByType: formData?.typeSpecificDataByType || {},
          currentProgramType: formData?.programType || 'hybrid',
          moduleConfiguration: formData?.moduleConfiguration
        };
        pricingServiceRef.current = new PricingService(initialState);
      } catch (error) {
        console.error('Failed to initialize PricingService:', error);
        // Fallback to default state
        const fallbackState = {
          typeSpecificDataByType: {
            hybrid: {
              programType: 'hybrid' as DeliveryMode,
              delivery: 'Hybrid',
              paymentOptions: [],
              programSchedule: {
                hybrid: {
                  startMonth: [],
                  startDate: [],
                  clinicalLocations: [],
                  practiceHours: 0,
                }
              },
              isConfigured: false,
              lastModified: new Date(),
            },
            'via-zoom': {
              programType: 'via-zoom' as DeliveryMode,
              delivery: 'Via-Zoom',
              paymentOptions: [],
              programSchedule: {
                'via-zoom': {
                  batches: [],
                  participantLimits: {},
                  sessionDuration: "",
                }
              },
              isConfigured: false,
              lastModified: new Date(),
            },
            'on-campus': {
              programType: 'on-campus' as DeliveryMode,
              delivery: 'On-Campus',
              paymentOptions: [],
              programSchedule: {
                'on-campus': {
                  batches: [],
                  facilityRequirements: [],
                  capacity: 0,
                }
              },
              isConfigured: false,
              lastModified: new Date(),
            },
            online: {
              programType: 'online' as DeliveryMode,
              delivery: 'Online',
              paymentOptions: [],
              programSchedule: {
                online: {
                  startMonth: [],
                  startDate: [],
                  enrollmentPeriods: [],
                }
              },
              isConfigured: false,
              lastModified: new Date(),
            }
          },
          currentProgramType: 'hybrid' as DeliveryMode,
          moduleConfiguration: undefined
        };
        pricingServiceRef.current = new PricingService(fallbackState);
      }
    }
    return pricingServiceRef.current;
  }, []); // Remove formData dependency to prevent infinite loops

  // Update pricing service when formData changes significantly (only when needed)
  useEffect(() => {
    if (pricingServiceRef.current && formData) {
      try {
        const currentService = pricingServiceRef.current;
        const newState = {
          typeSpecificDataByType: formData.typeSpecificDataByType || {},
          currentProgramType: formData.programType || 'hybrid',
          moduleConfiguration: formData.moduleConfiguration
        };
        
        // Only update if the data has actually changed significantly
        if (JSON.stringify(currentService.exportState()) !== JSON.stringify(newState)) {
          pricingServiceRef.current = new PricingService(newState);
        }
      } catch (error) {
        console.error('Failed to update PricingService:', error);
        // Don't crash the component, keep the existing service
      }
    }
  }, [formData?.typeSpecificDataByType, formData?.programType, formData?.moduleConfiguration]);

  /**
   * Create new payment options with Square integration
   */
  const createPaymentOptions = useCallback(async (paymentOptions: BasePaymentOption[]): Promise<PricingOperationResult> => {
    if (!formData) {
      return {
        success: false,
        errors: ['No form data available'],
        warnings: [],
      };
    }

    // Extract programId - can be from active program, inactive program, or draft
    // Backend handles all three cases when programId is provided
    const programId = formData.programId || formData.loadedProgramId || formData.draftId;

    // For new programs, we'll create Square plans without a program ID
    // The plans will be associated with the program when it's created
    if (!programId) {
      console.log('Creating Square plans for new program (no programId yet)');
      
      // Create a temporary program ID for Square API calls
      const tempProgramId = `temp_${Date.now()}`;
      
      try {
        const currentType = formData.programType as DeliveryMode;
        
        // Create payment options via API with temporary ID
        // Note: Backend will handle lookup for active, inactive, or draft programs
        const request: CreatePaymentOptionRequest = {
          programId: tempProgramId,
          programType: currentType,
          paymentOptions
        };

        const result = await PaymentOptionsAPI.createPaymentOptions(request);

        if (result.success && result.data) {
          // Update the payment options with Square IDs and slugs
          const enhancedPaymentOptions = result.data.paymentOptions.map(option => ({
            ...option,
            // Mark these as temporary plans that will be updated when program is created
            isTemporary: true,
            tempProgramId
          }));

          const successResult: PricingOperationResult = {
            success: true,
            data: { paymentOptions: enhancedPaymentOptions },
            errors: [],
            warnings: ['Square plans created successfully. They will be updated with the final program ID when the program is created.']
          };

          setLastOperationResult(successResult);
          return successResult;
        } else {
          return {
            success: false,
            errors: [result.error || 'Failed to create Square plans'],
            warnings: []
          };
        }
      } catch (error) {
        console.error('Error creating Square plans for new program:', error);
        return {
          success: false,
          errors: [`Failed to create Square plans: ${error instanceof Error ? error.message : 'Unknown error'}`],
          warnings: []
        };
      }
    }

    setIsLoading(true);

    try {
      const currentType = formData.programType as DeliveryMode;
      
      // Create payment options via API
      // Backend handles: active programs (by programId), inactive programs (by programId), or drafts (by draftId passed as programId)
      const request: CreatePaymentOptionRequest = {
        programId: programId,
        programType: currentType,
        paymentOptions
      };

      const result = await PaymentOptionsAPI.createPaymentOptions(request);

      if (result.success && result.data) {
        // Update form state with validated data from API
        const nextMap = { ...(formData.typeSpecificDataByType || {}) };
        nextMap[currentType] = {
          ...nextMap[currentType],
          paymentOptions: result.data.paymentOptions
        };

        setFormData((prev: any) => ({
          ...prev,
          typeSpecificData: {
            ...prev.typeSpecificData,
            paymentOptions: result.data!.paymentOptions
          },
          typeSpecificDataByType: nextMap
        }));

        // Show success message
        toast.success(`Payment options created for ${currentType} program type`);
        
        const successResult: PricingOperationResult = {
          success: true,
          data: result.data,
          errors: [],
          warnings: []
        };

        setLastOperationResult(successResult);
        return successResult;

      } else {
        // Show validation errors
        const errorResult: PricingOperationResult = {
          success: false,
          errors: [result.error || 'Failed to create payment options'],
          warnings: []
        };

        toast.error(result.error || 'Failed to create payment options');
        setLastOperationResult(errorResult);
        return errorResult;
      }

    } catch (error) {
      console.error('Error creating payment options:', error);
      const errorResult: PricingOperationResult = {
        success: false,
        errors: [`Failed to create payment options: ${error instanceof Error ? error.message : 'Unknown error'}`],
        warnings: [],
      };
      
      toast.error('Failed to create payment options. Please try again.');
      setLastOperationResult(errorResult);
      return errorResult;
    } finally {
      setIsLoading(false);
    }
  }, [formData, setFormData]);

  /**
   * Update temporary Square plans with final program ID when program is created
   */
  const updateTemporaryPlans = useCallback(async (finalProgramId: string): Promise<PricingOperationResult> => {
    if (!formData) {
      return {
        success: false,
        errors: ['No form data available'],
        warnings: [],
      };
    }

    try {
      const currentType = formData.programType as DeliveryMode;
      
      // Get payment options from form data
      const currentPaymentOptions = formData.typeSpecificData?.paymentOptions || [];
      
      // Find temporary payment options (check if they have temporary properties)
      const temporaryOptions = currentPaymentOptions.filter((option: BasePaymentOption & { isTemporary?: boolean }) => 
        option && typeof option === 'object' && 'isTemporary' in option && option.isTemporary
      );
      
      if (temporaryOptions.length === 0) {
        return {
          success: true,
          data: { paymentOptions: [] },
          errors: [],
          warnings: ['No temporary plans to update']
        };
      }

      // Update the temporary plans with the final program ID
      const updatedOptions = temporaryOptions.map((option: BasePaymentOption & { isTemporary?: boolean }) => ({
        ...option,
        programId: finalProgramId,
        isTemporary: false
      }));

      // Call API to update the plans with final program ID
      const request: UpdatePaymentOptionsRequest = {
        programId: finalProgramId,
        programType: currentType,
        paymentOptions: updatedOptions
      };

      const result = await PaymentOptionsAPI.updatePaymentOptions(request);

      if (result.success && result.data) {
        const successResult: PricingOperationResult = {
          success: true,
          data: result.data,
          errors: [],
          warnings: ['Temporary Square plans updated with final program ID successfully']
        };

        setLastOperationResult(successResult);
        return successResult;
      } else {
        return {
          success: false,
          errors: [result.error || 'Failed to update temporary plans with final program ID'],
          warnings: []
        };
      }
    } catch (error) {
      console.error('Error updating temporary plans:', error);
      return {
        success: false,
        errors: [`Failed to update temporary plans: ${error instanceof Error ? error.message : 'Unknown error'}`],
        warnings: []
      };
    }
  }, [formData]);

  /**
   * Update existing payment options with Square integration
   */
  const updatePaymentOptions = useCallback(async (paymentOptions: BasePaymentOption[]): Promise<PricingOperationResult> => {
    if (!formData) {
      return {
        success: false,
        errors: ['No form data available'],
        warnings: [],
      };
    }

    // Extract programId - can be from active program, inactive program, or draft
    const programId = formData.programId || formData.loadedProgramId || formData.draftId;
    
    if (!programId) {
      return {
        success: false,
        errors: ['Program ID is required to update payment options'],
        warnings: ['Please save the program first before updating payment options. For new programs, payment options are saved locally until the program is created.'],
      };
    }

    setIsLoading(true);

    try {
      const currentType = formData.programType as DeliveryMode;

      // Validate payment options locally first
      const validation = validatePaymentOptions(paymentOptions);
      if (!validation.isValid) {
        return {
          success: false,
          errors: validation.errors,
          warnings: validation.warnings,
        };
      }

      // Update payment options via API
      // Backend handles: active programs (by programId), inactive programs (by programId), or drafts (by draftId passed as programId)
      const request: UpdatePaymentOptionsRequest = {
        programId: programId,
        programType: currentType,
        paymentOptions
      };

      const result = await PaymentOptionsAPI.updatePaymentOptions(request);

      if (result.success && result.data) {
        // Update form state with validated data from API
        const nextMap = { ...(formData.typeSpecificDataByType || {}) };
        nextMap[currentType] = {
          ...nextMap[currentType],
          paymentOptions: result.data.paymentOptions
        };

        setFormData((prev: any) => ({
          ...prev,
          typeSpecificData: {
            ...prev.typeSpecificData,
            paymentOptions: result.data!.paymentOptions
          },
          typeSpecificDataByType: nextMap
        }));

        // Show success message
        toast.success(`Payment options updated for ${currentType} program type`);
        
        const successResult: PricingOperationResult = {
          success: true,
          data: result.data,
          errors: [],
          warnings: []
        };

        setLastOperationResult(successResult);
        return successResult;

      } else {
        // Show validation errors
        const errorResult: PricingOperationResult = {
          success: false,
          errors: [result.error || 'Failed to update payment options'],
          warnings: []
        };

        toast.error(result.error || 'Failed to update payment options');
        setLastOperationResult(errorResult);
        return errorResult;
      }

    } catch (error) {
      console.error('Error updating payment options:', error);
      const errorResult: PricingOperationResult = {
        success: false,
        errors: [`Failed to update payment options: ${error instanceof Error ? error.message : 'Unknown error'}`],
        warnings: [],
      };
      
      toast.error('Failed to update payment options. Please try again.');
      setLastOperationResult(errorResult);
      return errorResult;
    } finally {
      setIsLoading(false);
    }
  }, [formData, setFormData, getPricingService]);

  const rollbackLastOperation = useCallback((): PricingOperationResult => {
    if (!lastOperationResult?.rollbackData) {
      return {
        success: false,
        errors: ['No rollback data available'],
        warnings: [],
      };
    }

    const pricingService = getPricingService();
    const rollbackResult = pricingService.rollback(lastOperationResult.rollbackData);

    if (rollbackResult.success) {
      // Update form state with rolled back data
      setFormData((prev: any) => ({
        ...prev,
        typeSpecificData: rollbackResult.data?.typeSpecificData || prev.typeSpecificData,
        typeSpecificDataByType: rollbackResult.data?.typeSpecificDataByType || prev.typeSpecificDataByType
      }));

      toast.success('Last operation rolled back successfully');
    } else {
      rollbackResult.errors.forEach(error => toast.error(error));
    }

    return rollbackResult;
  }, [lastOperationResult, setFormData, getPricingService]);

  const getValidationErrors = useCallback((): string[] => {
    return lastOperationResult?.errors || [];
  }, [lastOperationResult]);

  const getValidationWarnings = useCallback((): string[] => {
    return lastOperationResult?.warnings || [];
  }, [lastOperationResult]);

  const isConfigured = useCallback((programType: DeliveryMode): boolean => {
    const pricingService = getPricingService();
    return pricingService.isConfigured(programType);
  }, [getPricingService]);

  const getPricingForType = useCallback((programType: DeliveryMode): any => {
    const pricingService = getPricingService();
    return pricingService.getPricingForType(programType);
  }, [getPricingService]);

  const validateAllPricing = useCallback((): { isValid: boolean; errors: string[]; warnings: string[] } => {
    const pricingService = getPricingService();
    return pricingService.validateAllPricing();
  }, [getPricingService]);

  return {
    updatePaymentOptions,
    createPaymentOptions,
    updateTemporaryPlans,
    rollbackLastOperation,
    getValidationErrors,
    getValidationWarnings,
    isConfigured,
    getPricingForType,
    validateAllPricing,
    isLoading
  };
};
